import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Grid,
  Snackbar,
  Switch,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import { useTenant } from "../../hooks/useTenant";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard";
import UnsavedChangesBar from "../shared/UnsavedChangesBar";
import LoadingComponent from "../../shared_modules/loadingComponent";

const BarberSchedule = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const api = useApi();
  const { user } = useAuth();
  const { tenant } = useTenant();

  // Owners/admins always edit. A barber is locked to read-only when the shop
  // has turned off "Allow barbers to edit their schedule" (server also enforces).
  const readOnly =
    user?.role === "barber" && tenant?.allow_barber_edit_schedule === false;

  const [originalSchedule, setOriginalSchedule] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null); // { message, severity }
  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
    type: "",
  });

  const hasChanges =
    JSON.stringify(schedule) !== JSON.stringify(originalSchedule);

  useUnsavedChangesGuard(hasChanges && !readOnly);

  const retrieveSchedule = async () => {
    if (!user?.user_id) return;
    setIsLoading(true);
    try {
      const res = await api(`/barbers/schedule.php?barber_id=${user.user_id}`, {
        method: "GET",
      });
      const hours = res.data.hours || [];
      setOriginalSchedule(hours);
      setSchedule(hours);
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      await api(`/barbers/schedule.php?barber_id=${user.user_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          hours: schedule.map((d) => ({
            day_of_week: d.day_of_week,
            is_working: d.is_working ? 1 : 0,
            start_time: d.is_working
              ? d.start_time?.slice(0, 5) || "09:00"
              : null,
            end_time: d.is_working ? d.end_time?.slice(0, 5) || "17:00" : null,
          })),
        }),
      });
      setOriginalSchedule(schedule);
      setSnack({ message: "Schedule saved.", severity: "success" });
    } catch (err) {
      setSnack({
        message: err.message || "Failed to save schedule.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => setSchedule(originalSchedule);

  useEffect(() => {
    retrieveSchedule();
  }, [user?.user_id]);

  const toggleDay = (index) => {
    const updated = [...schedule];
    updated[index] = {
      ...updated[index],
      is_working: updated[index].is_working ? 0 : 1,
    };
    setSchedule(updated);
  };

  const changeStartTime = (value, index) => {
    const updated = [...schedule];
    updated[index] = { ...updated[index], start_time: value };
    setSchedule(updated);
  };

  const changeEndTime = (value, index) => {
    const updated = [...schedule];
    updated[index] = { ...updated[index], end_time: value };
    setSchedule(updated);
  };

  const renderSchedule = () => (
    <Grid container>
      {schedule.map((day, index) => (
        <Box
          key={day.day_of_week}
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            opacity: day.is_working ? "1" : "0.6",
            backgroundColor: day.is_working ? "inherit" : "#f5f3ef",
            borderBottom:
              index !== schedule.length - 1 ? "0.1px solid" : undefined,
            borderColor: "customDivider",
            p: 2,
          }}
        >
          {/* Toggle + day name row */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Switch
                checked={!!day.is_working}
                onChange={() => toggleDay(index)}
                disabled={readOnly}
              />
              <Typography variant="subtitle1">{day.day_name}</Typography>
            </Box>

            {/* Desktop: time fields inline */}
            {day.is_working ? (
              <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1 }}>
                <TextField
                  variant="outlined"
                  placeholder="Start Time"
                  type="time"
                  value={day.start_time?.slice(0, 5) || "09:00"}
                  onChange={(e) => changeStartTime(e.target.value, index)}
                  disabled={readOnly}
                  sx={{ width: 150, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
                <Typography variant="body1">to</Typography>
                <TextField
                  variant="outlined"
                  placeholder="End Time"
                  type="time"
                  value={day.end_time?.slice(0, 5) || "17:00"}
                  onChange={(e) => changeEndTime(e.target.value, index)}
                  disabled={readOnly}
                  sx={{ width: 150, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </Box>
            ) : (
              <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                Not working
              </Typography>
            )}
          </Box>

          {/* Mobile: time fields on their own row */}
          {!!day.is_working && (
            <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1, mt: 1.5, pl: 1 }}>
              <TextField
                variant="outlined"
                placeholder="Start Time"
                type="time"
                value={day.start_time?.slice(0, 5) || "09:00"}
                onChange={(e) => changeStartTime(e.target.value, index)}
                disabled={readOnly}
                sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
              <Typography variant="body1">to</Typography>
              <TextField
                variant="outlined"
                placeholder="End Time"
                type="time"
                value={day.end_time?.slice(0, 5) || "17:00"}
                onChange={(e) => changeEndTime(e.target.value, index)}
                disabled={readOnly}
                sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Box>
          )}
        </Box>
      ))}
    </Grid>
  );

  return (
    <Box
      sx={{ maxWidth: 1100, mx: "auto", px: { xs: 3, md: 5 }, pt: 5, pb: 10 }}
    >
      <Box sx={{ mb: 1.5 }}>
        <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          Schedule
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Set the days and hours you're available to take appointments.
        </Typography>
      </Box>

      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your administrator manages the schedule for this shop. This page is
          read-only.
        </Alert>
      )}

      {showAlert.show && (
        <Alert
          sx={{ mb: 2 }}
          severity={showAlert.type}
          onClose={() => setShowAlert({ show: false, message: "", type: "" })}
        >
          {showAlert.message}
        </Alert>
      )}

      {isLoading ? (
        <LoadingComponent marginTop={10} />
      ) : schedule.length > 0 ? (
        <Box
          sx={{
            backgroundColor: "offWhite",
            p: 0,
            borderRadius: 2,
            border: "0.1px solid",
            borderColor: "customDivider",
            width: "100%",
            overflow: "hidden",
          }}
        >
          {renderSchedule()}
        </Box>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No schedule yet.
          </Typography>
        </Box>
      )}

      <UnsavedChangesBar
        open={hasChanges && !readOnly && !isLoading}
        saving={saving}
        onDiscard={discardChanges}
        onSave={saveSchedule}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack?.severity || "success"}
          variant="filled"
          onClose={() => setSnack(null)}
          sx={{ minWidth: 200 }}
        >
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BarberSchedule;
